import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Paper,
  Stack,
  Grid,
  Chip,
  Button,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import ElectricBoltRoundedIcon from "@mui/icons-material/ElectricBoltRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

export default function OverdriveLearning() {
  const { style } = useStyle();
  const [height, setHeight] = useState(window.innerHeight);
  const topContainer = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    topContainer.current?.scrollIntoView({ block: "start" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";
  const panelBg = "rgba(255,255,255,0.04)";
  const darkPanel = "rgba(0,0,0,0.18)";

  const articleSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Overdrive in Music Production: What It Is and How It Works",
      description:
        "Learn what overdrive is, how soft clipping works, how overdrive differs from distortion and fuzz, and how overdrive is used in amps, pedals, and modern production.",
      author: {
        "@type": "Organization",
        name: "Parish Audio",
      },
      publisher: {
        "@type": "Organization",
        name: "Parish Audio",
        logo: {
          "@type": "ImageObject",
          url: "https://parishaudio.com/assets/logo.png",
        },
      },
      mainEntityOfPage: "https://parishaudio.com/learning/overdrive",
      articleSection: "Audio Education",
      keywords: [
        "overdrive",
        "guitar overdrive",
        "soft clipping",
        "tube amp overdrive",
        "overdrive pedal",
      ],
    }),
    []
  );

  const quickFacts = [
    {
      title: "What it does",
      text: "Overdrive adds soft clipping, warmth, sustain, and grit while keeping more musical dynamics than heavier distortion styles.",
      icon: <ElectricBoltRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Why it matters",
      text: "It gives players touch-sensitive breakup that can move from nearly clean to rich crunch depending on how hard they play.",
      icon: <AutoAwesomeRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Where it is used",
      text: "Guitar, bass, synths, amp modeling, pedals, mix saturation, and tone shaping in many genres from blues to rock.",
      icon: <GraphicEqRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const overdriveMethods = [
    {
      name: "Pushed Tube Amplifier",
      description:
        "The classic source of overdrive. As an amp is driven past its clean headroom, it begins to clip in a smooth, musical way.",
    },
    {
      name: "Overdrive Pedal",
      description:
        "A pedal can simulate the sound and feel of a pushed amp, or push an amplifier even harder for more breakup and sustain.",
    },
    {
      name: "Pedal into Amp Combination",
      description:
        "Many players use both together, stacking gain so the pedal shapes the signal before it hits an already lively amplifier.",
    },
  ];

  const comparisons = [
    {
      name: "Overdrive",
      description:
        "Soft clipping, strong touch response, amp-like feel, and a smoother character that often cleans up when the input level drops.",
      link: null,
    },
    {
      name: "Distortion",
      description:
        "More aggressive and more uniform, often with heavier clipping and less dependence on playing dynamics.",
      link: "/learning/distortion",
    },
    {
      name: "Fuzz",
      description:
        "The most extreme of the three, with a buzzy, square-wave-like character and a more unruly or vintage texture.",
      link: "/learning/compare-distortion-overdrive-fuzz",
    },
  ];

  const ampFamilies = [
    {
      name: "British-Style Amps",
      description:
        "Often known for midrange-focused crunch and articulate rock tones. Marshall and Vox are classic examples.",
    },
    {
      name: "American-Style Amps",
      description:
        "Often associated with more clean headroom and wider tonal spread. Fender, Mesa/Boogie, and Dumble-inspired sounds are common references.",
    },
    {
      name: "Master Volume / High-Gain Designs",
      description:
        "Modern amplifiers often let players access preamp overdrive at more practical listening levels.",
    },
  ];

  const famousPedals = [
    {
      name: "Ibanez Tube Screamer",
      description:
        "A classic mid-forward overdrive widely used for blues, rock, and boosting solos.",
    },
    {
      name: "Klon Centaur",
      description:
        "Known for a more transparent style of overdrive that enhances the underlying amp character.",
    },
    {
      name: "Fulltone OCD",
      description:
        "Offers a broader range, from mild breakup into more forceful drive territory.",
    },
    {
      name: "Boss BD-2 Blues Driver",
      description:
        "Popular for its dynamic, amp-like feel and wide usefulness in clean-to-crunch playing.",
    },
  ];

  const toneControls = [
    {
      name: "Gain / Drive",
      description:
        "Controls how hard the clipping stage is pushed and how much breakup the effect produces.",
    },
    {
      name: "Tone / EQ",
      description:
        "Shapes the frequency balance of the driven sound, affecting bite, warmth, and cut.",
    },
    {
      name: "Level / Output",
      description:
        "Sets the overall volume after overdrive is applied and can also be used to push an amplifier harder.",
    },
    {
      name: "Pre-Drive EQ",
      description:
        "Adjusting frequencies before the gain stage changes what gets clipped more strongly.",
    },
    {
      name: "Post-Drive EQ",
      description:
        "Shapes the tone after clipping, which can help the sound sit better in a mix.",
    },
  ];

  const bestPractices = [
    "Start with less gain than you think you need. Overdrive often sounds bigger and clearer when it is not maxed out.",
    "Use the level control strategically. Sometimes the best result comes from lower pedal gain and higher output into the amp.",
    "Listen to how the overdrive reacts to picking intensity and your guitar volume knob.",
    "Shape the mids carefully if you want the tone to cut without sounding harsh.",
    "Try stacking a low-gain overdrive into a second gain stage for more complexity and control.",
  ];

  return (
    <>
      <Seo
        title="Overdrive | Parish Audio Learning"
        description="Learn what overdrive is, how soft clipping works, how overdrive differs from distortion and fuzz, and how overdrive is used in amps, pedals, and modern production."
        canonical="https://parishaudio.com/learning/overdrive"
        schema={articleSchema}
      />

      <Box sx={{ width: "95%", maxWidth: 1100, mx: "auto", minHeight: height - 140 }}>
        <Paper
          sx={{
            mt: 3,
            mb: 4,
            p: { xs: 2, md: 3 },
            background: `linear-gradient(180deg, ${style.color1} 0%, rgba(22,32,48,0.98) 100%)`,
            borderRadius: 3,
            color: style.color4,
            boxShadow: 12,
          }}
        >
          <Stack spacing={3}>
            <Box ref={topContainer}>
              <Chip
                label="Learning Center"
                sx={{
                  mb: 1.5,
                  color: style.color4,
                  backgroundColor: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              />

              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2rem", md: "3rem" },
                  fontWeight: 800,
                  lineHeight: 1.05,
                  mb: 1.25,
                  color: style.color4,
                }}
              >
                Overdrive
              </Typography>

              <Typography
                sx={{
                  color: style.color3,
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  lineHeight: 1.75,
                  maxWidth: 920,
                }}
              >
                Overdrive is one of the most important sounds in electric guitar
                history. It sits between clean tone and heavier distortion, giving
                players warmth, harmonic richness, and expressive breakup without
                completely flattening dynamics. Although it is closely related to
                distortion and fuzz, overdrive has its own feel, behavior, and
                musical purpose.
              </Typography>
            </Box>

            <Grid container spacing={2} alignItems="stretch">
              {quickFacts.map((item) => (
                <Grid item xs={12} md={4} key={item.title} sx={{ display: "flex" }}>
                  <Paper
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      backgroundColor: panelBg,
                      boxShadow: 6,
                      width: "100%",
                    }}
                  >
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      {item.icon}
                      <Box>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: style.color4,
                            mb: 0.4,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.6 }}>
                          {item.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Paper
              sx={{
                p: { xs: 2, md: 2.5 },
                borderRadius: 3,
                backgroundColor: panelBg,
                boxShadow: 8,
              }}
            >
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <GraphicEqRoundedIcon sx={{ color: accent }} />
                  <Typography
                    variant="h2"
                    sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                  >
                    What Is Overdrive?
                  </Typography>
                </Stack>

                <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                  Overdrive is the sound that occurs when an amplifier or drive
                  circuit is pushed beyond its clean headroom and begins to clip
                  in a relatively smooth way. Instead of sounding flat or brutally
                  chopped off, the waveform is rounded and enriched with new
                  harmonics, producing a tone that often feels warm, lively, and
                  responsive.
                </Typography>

                <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                  One of the defining traits of overdrive is that it often follows
                  the player. Lower input levels may stay fairly clean, while
                  harder picking or a hotter signal brings out more grit and bite.
                  That dynamic response is a big part of what makes overdrive feel
                  musical and expressive.
                </Typography>
              </Stack>
            </Paper>

            <Grid container spacing={2.25} alignItems="stretch">
              <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                <Paper
                  sx={{
                    p: { xs: 2, md: 2.5 },
                    borderRadius: 3,
                    backgroundColor: panelBg,
                    boxShadow: 8,
                    width: "100%",
                  }}
                >
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ElectricBoltRoundedIcon sx={{ color: accent }} />
                      <Typography
                        variant="h2"
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        How Overdrive Is Created
                      </Typography>
                    </Stack>

                    <Stack spacing={1.2}>
                      {overdriveMethods.map((item) => (
                        <Paper
                          key={item.name}
                          sx={{
                            p: 1.5,
                            borderRadius: 2.5,
                            backgroundColor: darkPanel,
                            boxShadow: 3,
                          }}
                        >
                          <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.4 }}>
                            {item.name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                            {item.description}
                          </Typography>
                        </Paper>
                      ))}
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      In amps, overdrive typically comes from tube stages reaching
                      their limits. In pedals, the same basic effect is simulated
                      with components such as op-amps, diodes, or transistors.
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                <Paper
                  sx={{
                    p: { xs: 2, md: 2.5 },
                    borderRadius: 3,
                    backgroundColor: panelBg,
                    boxShadow: 8,
                    width: "100%",
                  }}
                >
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CompareArrowsRoundedIcon sx={{ color: accent }} />
                      <Typography
                        variant="h2"
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Overdrive vs Distortion vs Fuzz
                      </Typography>
                    </Stack>

                    <Stack spacing={1.2}>
                      {comparisons.map((item) => (
                        <Paper
                          key={item.name}
                          sx={{
                            p: 1.5,
                            borderRadius: 2.5,
                            backgroundColor: darkPanel,
                            boxShadow: 3,
                          }}
                        >
                          <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.4 }}>
                            {item.link ? (
                              <Box
                                component={RouterLink}
                                to={item.link}
                                sx={{ color: style.color4, textDecoration: "none" }}
                              >
                                {item.name}
                              </Box>
                            ) : (
                              item.name
                            )}
                          </Typography>
                          <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                            {item.description}
                          </Typography>
                        </Paper>
                      ))}
                    </Stack>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>

            <Paper
              sx={{
                p: { xs: 2, md: 2.5 },
                borderRadius: 3,
                backgroundColor: panelBg,
                boxShadow: 8,
              }}
            >
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <AutoAwesomeRoundedIcon sx={{ color: accent }} />
                  <Typography
                    variant="h2"
                    sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                  >
                    Historical Context
                  </Typography>
                </Stack>

                <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                  Overdrive has roots in the early days of electric guitar, when
                  players simply turned their amplifiers up until the sound
                  started to break apart. Tube amplifiers were not originally
                  designed as “distortion devices,” but musicians discovered that
                  pushing them harder produced a tone that felt more alive,
                  energetic, and emotional.
                </Typography>

                <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                  As players wanted more control, manufacturers began designing
                  amplifiers and pedals that could create this breakup more
                  intentionally. That helped define the sounds of blues, classic
                  rock, and many later forms of guitar-based music.
                </Typography>
              </Stack>
            </Paper>

            <Grid container spacing={2.25} alignItems="stretch">
              <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                <Paper
                  sx={{
                    p: { xs: 2, md: 2.5 },
                    borderRadius: 3,
                    backgroundColor: panelBg,
                    boxShadow: 8,
                    width: "100%",
                  }}
                >
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <GraphicEqRoundedIcon sx={{ color: accent }} />
                      <Typography
                        variant="h2"
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Overdrive in Amplifiers
                      </Typography>
                    </Stack>

                    <Stack spacing={1.2}>
                      {ampFamilies.map((amp) => (
                        <Paper
                          key={amp.name}
                          sx={{
                            p: 1.5,
                            borderRadius: 2.5,
                            backgroundColor: darkPanel,
                            boxShadow: 3,
                          }}
                        >
                          <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.4 }}>
                            {amp.name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                            {amp.description}
                          </Typography>
                        </Paper>
                      ))}
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      Modern amps often include master volume controls, gain
                      stages, and channel switching so players can access
                      overdriven sounds without relying only on painfully high
                      stage volume.
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                <Paper
                  sx={{
                    p: { xs: 2, md: 2.5 },
                    borderRadius: 3,
                    backgroundColor: panelBg,
                    boxShadow: 8,
                    width: "100%",
                  }}
                >
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ElectricBoltRoundedIcon sx={{ color: accent }} />
                      <Typography
                        variant="h2"
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Famous Overdrive Pedals
                      </Typography>
                    </Stack>

                    <Stack spacing={1.2}>
                      {famousPedals.map((pedal) => (
                        <Paper
                          key={pedal.name}
                          sx={{
                            p: 1.5,
                            borderRadius: 2.5,
                            backgroundColor: darkPanel,
                            boxShadow: 3,
                          }}
                        >
                          <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.4 }}>
                            {pedal.name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                            {pedal.description}
                          </Typography>
                        </Paper>
                      ))}
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      Players often use overdrive pedals either as their main
                      source of breakup or as a boost into an already lively amp.
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>

            <Grid container spacing={2.25} alignItems="stretch">
              <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                <Paper
                  sx={{
                    p: { xs: 2, md: 2.5 },
                    borderRadius: 3,
                    backgroundColor: panelBg,
                    boxShadow: 8,
                    width: "100%",
                  }}
                >
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TipsAndUpdatesRoundedIcon sx={{ color: accent }} />
                      <Typography
                        variant="h2"
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Pedal Techniques and Touch Response
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      One of overdrive’s biggest strengths is its dynamic
                      behavior. Light playing can stay fairly clean, while harder
                      picking produces more crunch and sustain. That makes
                      overdrive feel connected to the player in a way that many
                      heavier distortion styles do not.
                    </Typography>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      Stacking overdrives is also common. A low-gain pedal can
                      push a second drive stage, shape the midrange, or tighten
                      the response before the sound reaches the amplifier.
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                <Paper
                  sx={{
                    p: { xs: 2, md: 2.5 },
                    borderRadius: 3,
                    backgroundColor: panelBg,
                    boxShadow: 8,
                    width: "100%",
                  }}
                >
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <GraphicEqRoundedIcon sx={{ color: accent }} />
                      <Typography
                        variant="h2"
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Tone Shaping and EQ
                      </Typography>
                    </Stack>

                    <Stack spacing={1.2}>
                      {toneControls.map((control) => (
                        <Paper
                          key={control.name}
                          sx={{
                            p: 1.5,
                            borderRadius: 2.5,
                            backgroundColor: darkPanel,
                            boxShadow: 3,
                          }}
                        >
                          <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.4 }}>
                            {control.name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                            {control.description}
                          </Typography>
                        </Paper>
                      ))}
                    </Stack>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>

            <Paper
              sx={{
                p: { xs: 2, md: 2.5 },
                borderRadius: 3,
                backgroundColor: panelBg,
                boxShadow: 8,
              }}
            >
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <AutoAwesomeRoundedIcon sx={{ color: accent }} />
                  <Typography
                    variant="h2"
                    sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                  >
                    Modern Innovations
                  </Typography>
                </Stack>

                <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                  Overdrive is no longer limited to turning up a vintage tube amp.
                  Modern production offers many approaches, including amp
                  modeling, hybrid analog-digital pedals, low-wattage amps,
                  attenuators, and software plugins. These tools make it easier to
                  get expressive driven tones at practical volumes and in more
                  controlled environments.
                </Typography>

                <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                  Even with all of these new options, the core appeal remains the
                  same: overdrive adds harmonic richness and emotional response
                  without completely erasing the player’s touch.
                </Typography>
              </Stack>
            </Paper>

            <Grid container spacing={2.25} alignItems="stretch">
              <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                <Paper
                  sx={{
                    p: { xs: 2, md: 2.5 },
                    borderRadius: 3,
                    backgroundColor: panelBg,
                    boxShadow: 8,
                    width: "100%",
                  }}
                >
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TipsAndUpdatesRoundedIcon sx={{ color: accent }} />
                      <Typography
                        variant="h2"
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Best Practices
                      </Typography>
                    </Stack>

                    <Box
                      component="ul"
                      sx={{
                        pl: 2.5,
                        m: 0,
                        color: style.color3,
                        "& li": { mb: 1.15, lineHeight: 1.75 },
                      }}
                    >
                      {bestPractices.map((tip, index) => (
                        <li key={index}>{tip}</li>
                      ))}
                    </Box>
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                <Paper
                  sx={{
                    p: { xs: 2, md: 2.5 },
                    borderRadius: 3,
                    backgroundColor: panelBg,
                    boxShadow: 8,
                    width: "100%",
                  }}
                >
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AutoAwesomeRoundedIcon sx={{ color: accent }} />
                      <Typography
                        variant="h2"
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Final Thoughts
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      Overdrive remains essential because it preserves expression
                      while adding tone. It can be subtle or assertive, vintage or
                      modern, and it works across a huge range of musical styles.
                    </Typography>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      Whether it comes from a tube amp, a pedal, or a plugin,
                      overdrive is about musical breakup rather than pure
                      destruction. That balance between warmth, grit, and touch is
                      what keeps it central to so many great sounds.
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>

            <Paper
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 3,
                backgroundColor: "rgba(255,255,255,0.03)",
                boxShadow: 4,
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
              >
                <Typography sx={{ color: style.color3, lineHeight: 1.7 }}>
                  Related reading: compare{" "}
                  <Box
                    component={RouterLink}
                    to="/learning/distortion"
                    sx={{ color: style.color4, textDecoration: "none", fontWeight: 700 }}
                  >
                    distortion
                  </Box>{" "}
                  and{" "}
                  <Box
                    component={RouterLink}
                    to="/learning/compare-distortion-overdrive-fuzz"
                    sx={{ color: style.color4, textDecoration: "none", fontWeight: 700 }}
                  >
                    fuzz
                  </Box>
                  .
                </Typography>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button
                    component={RouterLink}
                    to="/learning/distortion"
                    variant="outlined"
                    sx={{
                      borderColor: accent,
                      color: style.color4,
                      fontWeight: 700,
                      "&:hover": {
                        borderColor: style.color4,
                        backgroundColor: "rgba(255,255,255,0.04)",
                      },
                    }}
                  >
                    Distortion
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/learning/compare-distortion-overdrive-fuzz"
                    variant="outlined"
                    sx={{
                      borderColor: accent,
                      color: style.color4,
                      fontWeight: 700,
                      "&:hover": {
                        borderColor: style.color4,
                        backgroundColor: "rgba(255,255,255,0.04)",
                      },
                    }}
                  >
                    Compare Gain Types
                  </Button>
                </Stack>
              </Stack>
            </Paper>

            <Typography sx={{ textAlign: "center", color: style.color3, pt: 1 }}>
              * We are not affiliated with any brands or artists mentioned on this site. *
            </Typography>
          </Stack>
        </Paper>
      </Box>
    </>
  );
}


// import {React, useState, useContext, useEffect, useRef} from "react"
// import {useStyle} from '../../context/styleContext/styleContext'
// import ReactGA from 'react-ga4';
// import { Paper } from '@mui/material';
// import { Container, Typography, Divider, Box, List, ListItem, ListItemText } from "@mui/material";


// export default function OverdriveLearning(){

//   const { style, setStyle } = useStyle();
//   const [width, setWidth] = useState(window.innerWidth)

// function handleWindowSizeChange() {
//     setWidth(window.innerWidth);
// }
// useEffect(() => {
//     window.addEventListener('resize', handleWindowSizeChange);
//     return () => {
//         window.removeEventListener('resize', handleWindowSizeChange);
//     }
// }, []);

// const isMobile = width <= 768;


//   const [height, setHeight] = useState(window.innerHeight)

//   useEffect(() => {
//     function handleResize() {      
//       setWidth(window.innerWidth)
//       setHeight(window.innerHeight)
//   }
  
//     window.addEventListener('resize', handleResize)
//   })

// const topContainer = useRef();
//     useEffect(() => {
//         // To make sure page starts from the top
//         topContainer.current.scrollIntoView({ block: "end" });
//     }, []);




//     return(
//     <><div style={{margin:'auto', width:'95%', maxWidth:'800px'}}>

//     <h1 style={{marginTop:'25px'}} ref={topContainer} >Overdrive</h1>
//     <Divider className="mobile-card-divider" />
//     <p>Overdrive is one of the most iconic effects in electric guitar history. Spanning from the raw crunch of early rock 'n' roll to the expressive tones of modern blues and rock, overdrive has shaped the sound of popular music for decades. Often conflated with distortion and fuzz, overdrive occupies a distinct place in the spectrum of guitar tone. This document explores the history, mechanics, tonal characteristics, and use cases of the overdrive effect in detail, focusing specifically on overdrive as distinct from other forms of distortion.</p>
//     <h2>What is Overdrive?</h2>
//     <p>At its core, overdrive is the sound that occurs when an amplifier is pushed beyond its clean headroom. As the volume increases past a certain threshold, the signal begins to clip in a way that introduces harmonic richness and a warmer, more saturated tone. Unlike distortion pedals that provide a uniform clipping regardless of input level, overdrive typically scales with volume: it sounds cleaner at lower levels and increasingly distorted as the signal gets louder.</p>
//     <p>Overdrive can be achieved through:</p>
//     <ul class="a">
//         <li>Pushing a tube amplifier beyond its clean volume limit</li>
//         <li>Using an overdrive pedal to simulate that effect</li>
//         <li>A combination of both, with pedals enhancing an already cranked amp</li>
//     </ul>
//     <h2 style={{marginTop:'25px'}}>Historical Context</h2>
//     <p>The roots of overdrive trace back to the 1950s and '60s, when guitarists began turning up their amplifiers to achieve louder, grittier tones. These tube amps, not initially designed to distort, would begin to clip as their internal components were overdriven.</p>
//    <p>Early pioneers included:</p>
//     <ul class="a">
//         <li><strong><a href="https://www.chuckberry.com/" target="_blank" rel="noopener noreferrer">Chuck Berry </a></strong>, whose 1955 track "Maybellene" featured a naturally overdriven tone from a small tube amp</li>
//         <li><strong>Guitar Slim </strong> and <strong>Willie Johnson</strong>, who pushed their gear to create expressive, gritty sounds</li>
//         <li><strong><a href="https://www.keithrichards.com/" target="_blank" rel="noopener noreferrer">Keith Richards </a></strong>of <a href="https://rollingstones.com/" target="_blank" rel="noopener noreferrer">The Rolling Stones</a>, who used early overdrive and fuzz in classics like "(I Can’t Get No) Satisfaction"</li>
//     </ul>
//    <p>As the desire for controlled overdrive grew, manufacturers began designing amps and pedals specifically to replicate this saturated, warm tone.</p>
//     <h2>How Overdrive Works</h2>
//     <p>Overdrive results from soft clipping, a form of audio signal processing that rounds off the waveform peaks rather than abruptly chopping them (as with hard clipping found in distortion or fuzz). Soft clipping introduces both even and odd-order harmonics, producing a rich, musical timbre often described as "warm" or "organic."</p>
//         <p>In tube amplifiers, soft clipping occurs naturally as the tubes reach their operational limits. The key factors include:</p>
//     <ul class="a">
//         <li><strong>Plate voltage: </strong>Higher voltages allow more headroom before clipping</li>
//         <li><strong>Biasing: </strong>Affects when and how the tube starts to distort</li>
//         <li><strong>Multiple gain stages: </strong>Cascading gain stages can intensify the effect</li>

//     </ul>
//     <p>In overdrive pedals, this effect is simulated using solid-state components like diodes, op-amps, and transistors. Though some purists prefer the dynamics of tube-driven overdrive, many modern pedals do a remarkable job emulating the tonal behavior of a pushed amp.</p>
// <h2 style={{marginTop:'25px'}}>Overdrive vs. Distortion vs. Fuzz</h2>
// <p>While all three effects stem from the same basic principle—clipping an audio signal—they differ in execution and tonal results:</p>
// <ul class="a"> 
//         <li><strong>Overdrive: </strong> Emulates a pushed tube amp. More dynamic, warmer, and responsive to playing touch. Cleaner at low levels, dirtier as volume increases.</li>
//         <li><strong>Distortion: </strong> Applies more aggressive, consistent clipping. Often used in hard rock and metal for high-gain, saturated tones. Less dynamic.</li>
//         <li><strong>Fuzz: </strong>The most extreme form, often reshaping the waveform into a square-like wave. Produces thick, buzzy tones with heavy harmonic content and compression.</li>
// </ul>
// <p>Understanding these distinctions helps players select the right effect for their desired sound.</p>
// <h2>Overdrive in Amplifiers</h2>
// <h3>Tube Amps</h3>
// <p>The traditional way to achieve overdrive is by turning up a tube amp. Notable amp types include:</p>
// <ul class="a">
//         <li><strong>British-style amps:</strong>
//             <ul class="a">
//                 <li><strong><a href="https://www.marshall.com/" target="_blank" rel="noopener noreferrer">Marshall: </a></strong>Known for their mid-focused crunch, favored in rock</li>
//                 <li><strong><a href="https://voxamps.com/" target="_blank" rel="noopener noreferrer">Vox: </a></strong>Chimey and smooth, famously used by The Beatles and Queen</li>
//             </ul>
//         </li>
//         <li><strong>American-style amps:</strong>
//             <ul class="a">
//                 <li><strong><a href="https://www.fender.com/" target="_blank" rel="noopener noreferrer">Fender: </a></strong>Clean until pushed hard, often used with pedals</li>
//                 <li><strong><a href="https://www.mesaboogie.com/" target="_blank" rel="noopener noreferrer">Mesa/Boogie</a> & Dumble:</strong>Provide creamy, articulate overdrive with sculpted mids</li>
//             </ul>
//         </li>

// </ul>
// <p>These amps utilize EL34, EL84, 6L6, or 6V6 tubes, each with unique tonal characteristics.</p>
// <h3>Master Volume and Preamp Gain</h3>
// <p>Many modern amps offer master volume controls, allowing players to drive the preamp into overdrive without excessive loudness. Some also offer channel switching between clean and overdrive channels.</p>
// <h2 style={{marginTop:'25px'}}>Overdrive Pedals</h2>
// <p>Overdrive pedals emulate the sound of cranked amps and provide extra gain to drive an amp further. They are typically placed early in the signal chain and offer controls for gain, tone, and level. Famous overdrive pedals include:</p>
// <ul class="a">
//     <li><strong><a href="https://www.ibanez.com/usa/products/model/tube_screamer/" target="_blank" rel="noopener noreferrer">Ibanez Tube Screamer</a> (<a href="https://www.ibanez.com/usa/products/detail/ts808_99.html" target="_blank" rel="noopener noreferrer">TS808</a>, <a href="https://www.ibanez.com/usa/products/detail/ts9_99.html" target="_blank" rel="noopener noreferrer">TS9</a>): </strong>Mid-boosted, transparent overdrive, great for solos and blues</li>
//     <li><strong>Klon Centaur: </strong>Known for its "transparent" overdrive that enhances amp tone</li>
//     <li><strong><a href="https://www.fulltoneusa.com/products/custom-shop-ocd-v1-4" target="_blank" rel="noopener noreferrer">Fulltone OCD: </a></strong>Offers a wider range of tones, from mild breakup to near-distortion</li>
//     <li><strong><a href="https://www.boss.info/global/products/bd-2w/" target="_blank" rel="noopener noreferrer">Boss BD-2 Blues Driver: </a></strong>A dynamic, amp-like overdrive ideal for blues and classic rock</li>
// </ul>
// <p>Players often stack overdrives (use multiple pedals in series) to achieve layered gain stages and unique textures.</p>
// <h2 style={{marginTop:'25px'}}>Pedal Techniques and Stacking</h2>
// <p>Pedals can be used in two main ways:</p>
// <ul class="a">
//     <li><strong>Boosting an already overdriven amp: </strong>For solos or more saturation</li>
//     <li><strong>Creating overdrive into a clean amp:  </strong>For players using pedal platforms</li>
// </ul>
// <p>Stacking allows for:</p>
// <ul class="a">
//     <li>Low-gain pedals enhancing high-gain ones</li>
//     <li>Tone shaping by using EQ-focused overdrives</li>
//     <li>Greater control over midrange and dynamics</li>
// </ul>
// <h2>Dynamic Response and Touch Sensitivity</h2>
// <p>One of overdrive’s greatest assets is its ability to respond to playing dynamics. A lightly picked note may sound clean, while digging in creates crunch and bite. This allows expressive playing and dynamic phrasing without touching a knob. This trait is especially prominent in tube amp overdrive but can be emulated with well-designed pedals.</p>
// <h2 style={{marginTop:'25px'}}>Overdrive and EQ</h2>
// <p>EQ shapes how overdrive reacts:</p>
// <ul class="a">
//     <li><strong>Pre-overdrive EQ: </strong>Adjusting bass/mid/treble before the gain stage affects which frequencies clip more</li>
//     <li><strong>Post-overdrive EQ: </strong>Used to fine-tune the distorted signal</li>
//     <li><strong>Midrange emphasis: </strong>Helps solos cut through a mix</li>
//     <li><strong>Scooped mids: </strong>Creates a more aggressive, modern sound</li>
// </ul>
// <p>Some overdrives have built-in tone shaping controls, while others are paired with EQ pedals for further customization.</p>
// <h2 style={{marginTop:'25px'}}>Modern Innovations</h2>
// <p>Today’s gear offers more ways than ever to achieve overdrive:</p>
// <ul class="a">
//     <li><strong>Amp modeling units </strong> (e.g., <a href="https://line6.com/helix/" target="_blank" rel="noopener noreferrer">Line 6 Helix</a>, <a href="https://www.kemper-amps.com/profiler/overview" target="_blank" rel="noopener noreferrer">Kemper Profiler</a>) digitally emulate amp overdrive</li>
//     <li><strong>Hybrid pedals </strong>combine tube and solid-state components for unique textures</li>
//     <li><strong>Low-wattage amps </strong>allow players to achieve power tube overdrive at manageable volumes</li>
//     <li><strong>Attenuators </strong>let players crank amps without excessive stage volume</li>

// </ul>

// <p style={{marginTop:'35px'}}>Overdrive remains an essential tool in a guitarist’s sonic arsenal. Its versatility, warmth, and dynamic response make it ideal for a wide range of musical styles. Whether achieved through a tube amp, a pedal, or digital modeling, overdrive captures the essence of expressive electric guitar tone. Understanding its origins, mechanics, and applications allows players to harness its full potential and find their own voice within its harmonically rich landscape.</p>

//     <br />
//     <br />
//     <br />
//         <p style={{textAlign:'center'}}>* We are not affiliated with any of the brands or artists listed on this site *</p>
      
//       </div>
//        </>

//     )
// }

